import { useEffect, useState } from "react";
import api, { usd, formatApiErrorDetail } from "@/lib/api";
import { StatusBadge } from "@/components/RoleBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Copy, Printer, Settings2, Pencil, Archive, ArchiveRestore, Banknote, Landmark, Trash2, Eye } from "lucide-react";
import { impersonate } from "@/lib/impersonate";
import { ModulesDialog } from "@/components/ModulesDialog";
import { Puzzle } from "lucide-react";
import { TaxBankDialog } from "@/components/TaxBankDialog";
import { toast } from "sonner";
import { CompanyLogo } from "@/components/CompanyLogo";
import { KioskSettingsDialog } from "@/components/KioskSettingsDialog";
import { useCompany } from "@/context/CompanyContext";
import { AddressFields, parseAddress } from "@/components/AddressFields";

const SCHEDULES = ["weekly", "biweekly", "semimonthly", "monthly"];
const LOGOS = ["🏢", "🏭", "☁️", "🚀", "🏦", "🛒", "🏥", "⚙️"];

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", industry: "", pay_schedule: "biweekly", logo: "🏢", color: "#2563EB" });
  const [settingsFor, setSettingsFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [taxFor, setTaxFor] = useState(null);
  const [modsFor, setModsFor] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [staff, setStaff] = useState([]);
  const { refreshCompanies } = useCompany();

  const load = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/bureau/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const saveEdit = async () => {
    try {
      await api.put(`/companies/${editFor.id}`, { name: editFor.name, industry: editFor.industry, pay_schedule: editFor.pay_schedule, logo: editFor.logo, address_parts: editFor.address_parts || undefined });
      toast.success("Client updated"); setEditFor(null); load(); refreshCompanies();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleArchive = async (c) => {
    const archiving = c.status !== "archived";
    if (archiving && !window.confirm(`Archive ${c.name}? It will be hidden from kiosks and client lists (data is kept).`)) return;
    try { await api.post(`/companies/${c.id}/archive`); toast.success(archiving ? "Client archived" : "Client restored"); load(); refreshCompanies(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const removeCompany = async (c) => {
    const typed = window.prompt(`Permanently delete ${c.name} and ALL its employees, payroll runs, paystubs, users and documents? This cannot be undone.\n\nType the company name to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== c.name) { toast.error("Name did not match — nothing deleted"); return; }
    try { const { data } = await api.delete(`/companies/${c.id}`); toast.success(`${c.name} deleted (${data.documents_deleted} records, ${data.users_deleted} users)`); load(); refreshCompanies(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendMicr = async (c) => {
    const to = window.prompt(`Send the MICR check printing setup form for ${c.name} to (HR manager email):`, "");
    if (to === null) return;
    try { const { data } = await api.post("/check-setup/send", { company_id: c.id, to_email: to || null }); toast.success(`Check setup form emailed to ${data.to_email}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveLag = async (c, val) => {
    const n = Number(val);
    if (Number.isNaN(n) || n === (c.pay_lag_days ?? 5)) return;
    try { await api.put(`/companies/${c.id}`, { pay_lag_days: n }); toast.success(`${c.name}: payday is now ${n} day${n === 1 ? "" : "s"} after period end`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const visible = companies.filter((c) => showArchived || c.status !== "archived");

  const assignPM = async (cid, uid) => {
    try {
      await api.put(`/companies/${cid}/payroll-manager`, { user_id: uid === "none" ? null : uid });
      toast.success("Payroll manager assigned");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyLink = (id) => {
    const url = `${window.location.origin}/timeclock/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Time clock link copied to clipboard");
  };

  const create = async () => {
    try {
      await api.post("/companies", form);
      toast.success("Client company created");
      setOpen(false);
      setForm({ name: "", industry: "", pay_schedule: "biweekly", logo: "🏢", color: "#2563EB" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Client Companies</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage the tenants your bureau serves.</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer"><input type="checkbox" data-testid="show-archived-toggle" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-primary" /> Show archived</label>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-client-btn"><Plus className="w-4 h-4 mr-1.5" /> New Client</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-client-modal">
            <DialogHeader><DialogTitle className="font-display">Add Client Company</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Company name</Label>
                <Input data-testid="client-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Globex Inc." />
              </div>
              <div className="space-y-1.5">
                <Label>Industry</Label>
                <Input data-testid="client-industry-input" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Technology" />
              </div>
              <AddressFields testid="client-addr" label="Business address" value={form.address_parts} onChange={(a) => setForm({ ...form, address_parts: a })} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Pay schedule</Label>
                  <Select value={form.pay_schedule} onValueChange={(v) => setForm({ ...form, pay_schedule: v })}>
                    <SelectTrigger data-testid="client-schedule-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Icon</Label>
                  <Select value={form.logo} onValueChange={(v) => setForm({ ...form, logo: v })}>
                    <SelectTrigger data-testid="client-logo-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOGOS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="client-save-btn" onClick={create} disabled={!form.name}>Create Client</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((c) => (
          <Card key={c.id} data-testid={`company-card-${c.id}`} className={`p-5 hover:shadow-md transition-shadow ${c.status === "archived" ? "opacity-60" : ""}`}>
            <div className="flex items-center gap-3">
              <CompanyLogo company={c} />
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate font-display">{c.name}{c.client_id && <span data-testid={`client-id-${c.id}`} className="ml-1.5 text-[10px] font-mono-num text-muted-foreground">#{c.client_id}</span>}</div>
                <div className="text-xs text-muted-foreground">{c.industry}</div>
              </div>
              <button data-testid={`impersonate-client-${c.id}`} title="View as this client (HR view)" onClick={() => impersonate({ company_id: c.id })} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Eye className="w-4 h-4" /></button>
              <button data-testid={`edit-client-${c.id}`} title="Edit client" onClick={() => setEditFor({ ...c })} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Pencil className="w-4 h-4" /></button>
              <button data-testid={`modules-client-${c.id}`} title="Modules & add-ons" onClick={() => setModsFor(c)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Puzzle className="w-4 h-4" /></button>
              <button data-testid={`tax-bank-${c.id}`} title="Tax & bank details" onClick={() => setTaxFor(c)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground"><Landmark className="w-4 h-4" /></button>
              <button data-testid={`archive-client-${c.id}`} title={c.status === "archived" ? "Restore client" : "Archive client"} onClick={() => toggleArchive(c)} className="p-2 rounded-lg hover:bg-accent text-muted-foreground">{c.status === "archived" ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}</button>
              <button data-testid={`delete-client-${c.id}`} title="Delete client permanently" onClick={() => removeCompany(c)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm">
              <div>
                <div className="text-xs text-muted-foreground">Employees</div>
                <div className="font-mono-num font-semibold">{c.employee_count}</div>
                <div className="col-span-full flex flex-wrap gap-1 mt-1" data-testid={`modules-badges-${c.id}`}>{[["payroll", "Payroll"], ["tax_impound", "Tax Impound"], ["hiring", "Hiring"], ["time_attendance", "Time & Attendance"], ["hr", "HR Center"]].filter(([k]) => c.modules?.[k]).map(([k, l]) => <span key={k} className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{l}</span>)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Schedule</div>
                <div className="capitalize font-medium">{c.pay_schedule}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Payday lag</div>
                <div className="flex items-center gap-1 justify-end"><span className="text-xs text-muted-foreground">+</span>
                  <Input data-testid={`pay-lag-${c.id}`} type="number" min={0} max={30} className="h-7 w-14 px-1.5 text-center font-mono-num" defaultValue={c.pay_lag_days ?? 5} onBlur={(e) => saveLag(c, e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
                  <span className="text-xs text-muted-foreground">days</span></div>
              </div>
              <StatusBadge status={c.status} />
            </div>
            <div className="mt-4 space-y-1">
              <Label className="text-xs text-muted-foreground">Payroll Manager</Label>
              <Select value={c.payroll_manager_id || "none"} onValueChange={(v) => assignPM(c.id, v)}>
                <SelectTrigger data-testid={`pm-select-${c.id}`} className="h-9"><SelectValue placeholder="Assign…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button data-testid={`micr-setup-${c.id}`} onClick={() => sendMicr(c)} className={`mt-3 w-full flex items-center justify-center gap-1.5 text-xs font-medium px-2 py-2 rounded-lg border transition-colors ${c.check_printing ? "border-emerald-300 text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10" : "border-border hover:bg-accent"}`}>
              <Banknote className="w-3.5 h-3.5" /> {c.check_printing ? `MICR checks ready · ${c.check_printing.bank_name} ····${c.check_printing.account_last4} · next #${c.check_printing.next_check_number}` : "Send MICR check setup form"}
            </button>
            <div className="mt-2 grid grid-cols-3 gap-2">
              <button data-testid={`copy-kiosk-${c.id}`} onClick={() => copyLink(c.id)} title="Copy time clock link"
                className="flex items-center justify-center gap-1.5 text-xs font-medium px-2 py-2 rounded-lg border border-border hover:bg-accent transition-colors">
                <Copy className="w-3.5 h-3.5" /> Link
              </button>
              <a data-testid={`print-kit-${c.id}`} href={`/kiosk-kit/${c.id}`} target="_blank" rel="noreferrer" title="Print PIN sheet & QR"
                className="flex items-center justify-center gap-1.5 text-xs font-medium px-2 py-2 rounded-lg border border-border hover:bg-accent transition-colors">
                <Printer className="w-3.5 h-3.5" /> Kit
              </a>
              <button data-testid={`kiosk-settings-${c.id}`} onClick={() => setSettingsFor(c)} title="Kiosk & branding settings"
                className="flex items-center justify-center gap-1.5 text-xs font-medium px-2 py-2 rounded-lg border border-border hover:bg-accent transition-colors">
                <Settings2 className="w-3.5 h-3.5" /> Settings
              </button>
            </div>
          </Card>
        ))}
      </div>
      <KioskSettingsDialog open={!!settingsFor} onOpenChange={(o) => !o && setSettingsFor(null)} company={settingsFor}
        onSaved={() => { load(); refreshCompanies(); }} />

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent data-testid="edit-client-modal">
          <DialogHeader><DialogTitle className="font-display">Edit Client</DialogTitle></DialogHeader>
          {editFor && (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Company name</Label><Input data-testid="edit-client-name" value={editFor.name} onChange={(e) => setEditFor({ ...editFor, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Industry</Label><Input data-testid="edit-client-industry" value={editFor.industry || ""} onChange={(e) => setEditFor({ ...editFor, industry: e.target.value })} /></div>
              <AddressFields testid="edit-client-addr" label="Business address" value={editFor.address_parts || parseAddress(editFor.address)} onChange={(a) => setEditFor({ ...editFor, address_parts: a })} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Pay schedule</Label>
                  <Select value={editFor.pay_schedule} onValueChange={(v) => setEditFor({ ...editFor, pay_schedule: v })}>
                    <SelectTrigger data-testid="edit-client-schedule"><SelectValue /></SelectTrigger>
                    <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Icon</Label>
                  <Select value={editFor.logo} onValueChange={(v) => setEditFor({ ...editFor, logo: v })}>
                    <SelectTrigger data-testid="edit-client-logo"><SelectValue /></SelectTrigger>
                    <SelectContent>{LOGOS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="edit-client-save" onClick={saveEdit} disabled={!editFor?.name}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ModulesDialog company={modsFor} onOpenChange={(o) => !o && setModsFor(null)} onSaved={() => { load(); refreshCompanies(); }} />
      <TaxBankDialog company={taxFor} onClose={() => setTaxFor(null)} onSaved={load} />

    </div>
  );
}
